/**
 * Tender Terms API Service
 * 
 * Provides standardized methods for interacting with the tender terms API endpoints
 */
import api from './api';

/**
 * Tender terms service with standardized methods for tender terms-related API operations
 */
export const tenderTermsService = {
  /**
   * Get terms library for global terms
   * @returns {Promise<Object>} Terms library items
   */
  async getTermsLibrary() {
    return api.get('terms-library');
  },

  /**
   * Add an item to the terms library
   * @param {string} type - Type of term (inclusion, exclusion, qualification)
   * @param {string} text - Term text
   * @returns {Promise<Object>} Created library item
   */
  async addToLibrary(type, text) {
    return api.post('terms-library', { type, text });
  },

  /**
   * Get terms for a specific tender
   * @param {number|string} tenderId - Tender ID
   * @returns {Promise<Object>} Tender terms
   */
  async getTenderTerms(tenderId) {
    return api.get(`tenders/${tenderId}/terms`);
  },

  /**
   * Update tender terms
   * @param {number|string} tenderId - Tender ID
   * @param {Object} termsData - Terms data to update
   * @returns {Promise<Object>} Updated tender terms
   */
  async updateTenderTerms(tenderId, termsData) {
    return api.put(`tenders/${tenderId}/terms`, termsData);
  },

  /**
   * Get terms categories for a tender quote
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @returns {Promise<Array>} Terms categories
   */
  async getTermsCategories(tenderId, quoteId) {
    return api.get(`tenders/${tenderId}/quotes/${quoteId}/terms/categories`);
  },

  /**
   * Create a terms category for a tender quote
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {Object} categoryData - Category data
   * @returns {Promise<Object>} Created category
   */
  async createCategory(tenderId, quoteId, categoryData) {
    return api.post(`tenders/${tenderId}/quotes/${quoteId}/terms/categories`, categoryData);
  },

  /**
   * Update a terms category
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} categoryId - Category ID
   * @param {Object} categoryData - Updated category data
   * @returns {Promise<Object>} Updated category
   */
  async updateCategory(tenderId, quoteId, categoryId, categoryData) {
    return api.put(`tenders/${tenderId}/quotes/${quoteId}/terms/categories/${categoryId}`, categoryData);
  },

  /**
   * Delete a terms category
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} categoryId - Category ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteCategory(tenderId, quoteId, categoryId) {
    return api.delete(`tenders/${tenderId}/quotes/${quoteId}/terms/categories/${categoryId}`);
  },

  /**
   * Create a terms item
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} categoryId - Category ID
   * @param {Object} itemData - Item data
   * @returns {Promise<Object>} Created item
   */
  async createItem(tenderId, quoteId, categoryId, itemData) {
    return api.post(`tenders/${tenderId}/quotes/${quoteId}/terms/categories/${categoryId}/items`, itemData);
  },

  /**
   * Update a terms item
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} categoryId - Category ID
   * @param {number|string} itemId - Item ID
   * @param {Object} itemData - Updated item data
   * @returns {Promise<Object>} Updated item
   */
  async updateItem(tenderId, quoteId, categoryId, itemId, itemData) {
    return api.put(`tenders/${tenderId}/quotes/${quoteId}/terms/categories/${categoryId}/items/${itemId}`, itemData);
  },

  /**
   * Delete a terms item
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} categoryId - Category ID
   * @param {number|string} itemId - Item ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteItem(tenderId, quoteId, categoryId, itemId) {
    return api.delete(`tenders/${tenderId}/quotes/${quoteId}/terms/categories/${categoryId}/items/${itemId}`);
  },

  /**
   * Reorder items within a category
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} categoryId - Category ID
   * @param {Array} items - Array of items with new order
   * @returns {Promise<Object>} Reorder result
   */
  async reorderItems(tenderId, quoteId, categoryId, items) {
    return api.put(`tenders/${tenderId}/quotes/${quoteId}/terms/categories/${categoryId}/items`, items);
  },
};

export default tenderTermsService;